import React, { useState, useEffect, useMemo } from 'react';
import { Search as SearchIcon, OpenInNew as OpenInNewIcon } from '@mui/icons-material';
import DepartureTopStrip from '../components/ui/DepartureTopStrip';
import PhoneTopStrip from '../components/ui/PhoneTopStrip';
import useRenderMode from '../hooks/useRenderMode';
import { useZones, getSupervisorPrimaryZoneName } from '../hooks/useZones';
import { collection, getDocs, getFirestore } from 'firebase/firestore';
import * as XLSX from 'xlsx-js-style';
import {
  STYLE, buildSheet, downloadWorkbook, makeWorkbook,
  appendSheet, titleBlock, footerBlock, zebraStripe,
} from '../utils/excelReport';

// ---------------------------------------------------------------------------
// Canonical employee management has moved to IxoraOne.
// This page is a READ-ONLY directory — every CRUD action ("+ Add Supervisor",
// EDIT, DELETE) used to live here was removed in May 2026 when IxoraOne
// became the single source of truth for employee records (creation,
// role assignment, deactivation, password reset, face enrollment, etc.).
//
// What remains here:
//   - Read-only list of the supervisors collection (sourced from Firestore)
//   - Filters: search by name/ID, area, role, status
//   - Excel export of the filtered view
//   - Per-row "↗ Manage in IxoraOne" deep-link
//
// Per-row link target: https://one.ixoragroup.com/employees/{ixoraone_uid}
// when the supervisor is linked to an IxoraOne employee (via the embed-
// canonical-bridge script), otherwise the IxoraOne employees list.
// ---------------------------------------------------------------------------

const IXORAONE_BASE = 'https://one.ixoragroup.com';
const IXORAONE_EMPLOYEES_URL = `${IXORAONE_BASE}/employees`;

function initialsAvatar(name, id) {
  const text = (name || id || '?').trim();
  const parts = text.split(/\s+/);
  const initials = parts.slice(0, 2).map(w => w[0].toUpperCase()).join('');
  let hash = 0;
  for (let i = 0; i < text.length; i++) hash = text.charCodeAt(i) + ((hash << 5) - hash);
  const PALETTE = ['#EA580C', '#C2410C', '#D97706', '#9A3412', '#B45309', '#059669', '#0284C7', '#7C3AED'];
  const bg = PALETTE[Math.abs(hash) % PALETTE.length];
  return (
    <div aria-hidden="true" style={{
      width: 28, height: 28, borderRadius: '50%',
      background: bg, color: '#fff', flexShrink: 0,
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      fontSize: 10, fontWeight: 700, letterSpacing: '-0.01em',
    }}>
      {initials}
    </div>
  );
}

function ixoraOneLinkFor(supervisor) {
  if (supervisor?.ixoraone_uid) {
    return `${IXORAONE_EMPLOYEES_URL}/${supervisor.ixoraone_uid}`;
  }
  return IXORAONE_EMPLOYEES_URL;
}

const ManageSupervisors = () => {
  const mode = useRenderMode();
  const [supervisors, setSupervisors] = useState([]);
  const [filteredSupervisors, setFilteredSupervisors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedAreaFilter, setSelectedAreaFilter] = useState('All');
  const [selectedRoleFilter, setSelectedRoleFilter] = useState('All');
  const [selectedStatusFilter, setSelectedStatusFilter] = useState('All');

  // Zone list comes from Firestore. 'Manager' is a special non-zone allotment
  // that supervisors can be tagged with, so we keep it as a sentinel option.
  const { zones, zoneById, zoneByAreaNumber } = useZones();
  const areaOptions = useMemo(
    () => ['All', 'Manager', ...zones.map((z) => z.name)],
    [zones],
  );

  // Resolves a supervisor's effective zone (dual-read: new allotted_site_ids
  // first, legacy ixora_allotment fallback). Centralised so every callsite
  // gets the same answer.
  const supervisorZoneName = (s) =>
    getSupervisorPrimaryZoneName(s, zoneById, zoneByAreaNumber);

  // -------------------------------------------------------------------------
  // Data
  // -------------------------------------------------------------------------

  useEffect(() => {
    const fetchSupervisors = async () => {
      setLoading(true);
      try {
        const db = getFirestore();
        const querySnapshot = await getDocs(collection(db, 'supervisors'));
        const supervisorsList = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        supervisorsList.sort((a, b) => {
          if (!a.ixora_user_id) return 1;
          if (!b.ixora_user_id) return -1;
          return a.ixora_user_id.localeCompare(b.ixora_user_id, undefined, { numeric: true });
        });
        setSupervisors(supervisorsList);
        setFilteredSupervisors(supervisorsList);
        setError(null);
      } catch (err) {
        console.error('Error fetching supervisors:', err);
        setError('Failed to load supervisor data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    fetchSupervisors();
  }, []);

  // -------------------------------------------------------------------------
  // Area helpers — Feedback uses numeric codes; we look them up against the
  // 7 GHMC zones loaded from Firestore via useZones.
  // -------------------------------------------------------------------------

  const _getAreaNumber = (areaName) => {
    // Aliases keep old supervisor docs working that store the short name.
    const ALIASES = {
      'Police Station': 'Police Stations - Hyderabad',
      'Police Station Hyderabad': 'Police Stations - Hyderabad',
      'Police Station Cyberabad': 'Police Stations - Cyberabad',
    };
    const canonical = ALIASES[areaName] ?? areaName;
    const z = zones.find((zone) => zone.name === canonical);
    return z ? String(z.areaNumber) : null;
  };

  const getAreaName = (areaNumber) => {
    if (!areaNumber) return '—';
    const trimmed = String(areaNumber).trim();
    const n = parseInt(trimmed, 10);
    if (Number.isFinite(n)) {
      const z = zones.find((zone) => zone.areaNumber === n);
      if (z) return z.name;
    }
    // If it's already an area name (some supervisor docs store the name), return it as-is.
    return trimmed;
  };

  // -------------------------------------------------------------------------
  // Filters
  // -------------------------------------------------------------------------

  useEffect(() => {
    let result = [...supervisors];
    if (searchTerm.trim() !== '') {
      const lower = searchTerm.toLowerCase();
      result = result.filter(s =>
        (s.display_name && s.display_name.toLowerCase().includes(lower)) ||
        (s.ixora_user_id && s.ixora_user_id.toLowerCase().includes(lower)) ||
        (s.ixoraone_employee_id && s.ixoraone_employee_id.toLowerCase().includes(lower))
      );
    }
    if (selectedAreaFilter !== 'All') {
      if (selectedAreaFilter === 'Manager') {
        result = result.filter((s) => String(s.ixora_allotment || '').trim().toLowerCase() === 'manager');
      } else {
        result = result.filter((s) => supervisorZoneName(s) === selectedAreaFilter);
      }
    }
    if (selectedRoleFilter !== 'All') {
      if (selectedRoleFilter === 'Site Manager') result = result.filter(s => s.userRole === 'siteManager');
      else result = result.filter(s => !s.userRole || s.userRole === 'supervisor');
    }
    if (selectedStatusFilter !== 'All') {
      if (selectedStatusFilter === 'Blocked') result = result.filter(s => s.userStatus === 'blocked' || s.userStatus === 'inactive');
      else result = result.filter(s => !s.userStatus || s.userStatus === 'active');
    }
    setFilteredSupervisors(result);
  }, [searchTerm, selectedAreaFilter, selectedRoleFilter, selectedStatusFilter, supervisors]);

  // -------------------------------------------------------------------------
  // Export — same XLS report as before; no CRUD impact.
  // -------------------------------------------------------------------------

  const handleExport = async () => {
    try {
      const COLS = 8;
      const tb = titleBlock({
        title: 'IXORA · SUPERVISOR DIRECTORY',
        subtitle: `${filteredSupervisors.length} SUPERVISORS  ·  GENERATED ${new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }).toUpperCase()}`,
        spanCols: COLS,
      });

      const headerRow = [
        { v: 'Sr', s: STYLE.headerPrimary },
        { v: 'IxoraOne ID', s: STYLE.headerPrimary },
        { v: 'Name', s: STYLE.headerPrimary },
        { v: 'Area Allotment', s: STYLE.headerPrimary },
        { v: 'Phone', s: STYLE.headerPrimary },
        { v: 'Last Checklist', s: STYLE.headerPrimary },
        { v: 'Role', s: STYLE.headerPrimary },
        { v: 'Status', s: STYLE.headerPrimary },
      ];

      const aoa = [...tb.rows, headerRow];

      let activeCount = 0, blockedCount = 0;
      filteredSupervisors.forEach((s, idx) => {
        const status = (s.userStatus === 'blocked' || s.userStatus === 'inactive') ? 'Blocked' : 'Active';
        if (status === 'Blocked') blockedCount++; else activeCount++;
        aoa.push([
          { v: idx + 1, t: 'n', s: STYLE.bodyId },
          { v: s.ixoraone_employee_id || s.ixora_user_id || '—', s: STYLE.bodyId },
          { v: s.display_name || '—', s: STYLE.bodyText },
          { v: supervisorZoneName(s) ?? (s.ixora_allotment ? getAreaName(s.ixora_allotment) : '—'), s: STYLE.bodyTextCenter },
          { v: s.phone_number || '—', s: STYLE.bodyId },
          { v: s.lastChecklistDocDate ? new Date(s.lastChecklistDocDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—', s: STYLE.bodyTextCenter },
          { v: s.userRole === 'siteManager' ? 'Site Manager' : 'Supervisor', s: STYLE.bodyTextCenter },
          { v: status, s: status === 'Active' ? STYLE.statusDone : STYLE.statusNotDone },
        ]);
      });

      aoa.push([
        { v: 'TOTAL', s: STYLE.totalsRow },
        { v: '', s: STYLE.totalsRow },
        { v: `${filteredSupervisors.length} supervisors`, s: STYLE.totalsRow },
        { v: '', s: STYLE.totalsRow },
        { v: '', s: STYLE.totalsRow },
        { v: '', s: STYLE.totalsRow },
        { v: '', s: STYLE.totalsRow },
        { v: `${activeCount} active · ${blockedCount} blocked`, s: STYLE.totalsRow },
      ]);

      const fb = footerBlock({
        generatedBy: 'feedback dashboard',
        contextLine: 'Source: supervisors collection  ·  Canonical record: IxoraOne (one.ixoragroup.com)',
        spanCols: COLS,
      });
      const fbBase = aoa.length;
      aoa.push(...fb.rows);

      const dataStart = 4;
      const dataEnd = dataStart + filteredSupervisors.length - 1;
      zebraStripe(aoa.slice(0, dataEnd + 1), dataStart);

      const headerRef = XLSX.utils.encode_cell({ r: 3, c: 0 });
      const lastDataRef = XLSX.utils.encode_cell({ r: dataEnd, c: COLS - 1 });

      const ws = buildSheet({
        aoa,
        cols: [
          { wch: 5 }, { wch: 14 }, { wch: 26 }, { wch: 22 },
          { wch: 14 }, { wch: 16 }, { wch: 14 }, { wch: 12 },
        ],
        merges: [...tb.merges, ...fb.merges(fbBase)],
        freeze: { ySplit: 4 },
        autoFilter: `${headerRef}:${lastDataRef}`,
      });
      ws['!rows'] = [{ hpt: 28 }, { hpt: 18 }, { hpt: 6 }, { hpt: 22 }];

      const wb = makeWorkbook();
      appendSheet(wb, ws, 'Supervisors');
      const fileDate = new Date().toISOString().slice(0, 10);
      downloadWorkbook(wb, `Ixora_Supervisors_${fileDate}.xlsx`);
    } catch (e) {
      console.error('Export failed:', e);
      alert('Failed to export. Please try again.');
    }
  };

  // -------------------------------------------------------------------------
  // Read-only banner — surfaces the architectural shift.
  // -------------------------------------------------------------------------

  const ManagedInIxoraOneBanner = () => (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', minWidth: 0,
      padding: '10px 14px',
      background: 'var(--c-bg-2, #faf7f2)',
      border: '1px solid var(--c-line-2)',
      borderLeft: '3px solid #EA580C',
      fontFamily: "'Geist', sans-serif",
      fontSize: 12,
      color: 'var(--c-ink-2)',
      lineHeight: 1.4,
    }}>
      <span style={{
        fontFamily: 'var(--font-mono)', fontSize: 10, letterSpacing: '.14em',
        textTransform: 'uppercase', color: '#EA580C', fontWeight: 700,
      }}>
        Read-only
      </span>
      <span style={{ flex: 1 }}>
        Supervisor accounts are now managed in IxoraOne. To add, edit, deactivate,
        or reset a password, use the IxoraOne employees module.
      </span>
      <a
        href={IXORAONE_EMPLOYEES_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="db-pill primary"
        style={{ textDecoration: 'none', whiteSpace: 'nowrap' }}
      >
        Open IxoraOne <OpenInNewIcon style={{ fontSize: 14, marginLeft: 4, verticalAlign: 'middle' }} />
      </a>
    </div>
  );

  // -------------------------------------------------------------------------
  // Table (desk + reused in scrollable wrapper)
  // -------------------------------------------------------------------------

  const renderTableSection = () => (
    <div style={{ overflowX: 'auto', width: '100%' }}>
      <table className="db-table" style={{ minWidth: 900 }}>
        <thead>
          <tr>
            <th style={{ width: 40 }}></th>
            <th className="num">ID</th>
            <th>NAME</th>
            <th>AREA</th>
            <th className="num">PHONE</th>
            <th className="num">LAST CHECKLIST</th>
            <th>ROLE</th>
            <th>STATUS</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr><td colSpan={9} style={{ textAlign: 'center', padding: '2rem' }}>
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 10, fontFamily: 'var(--font-mono)', fontSize: 11, letterSpacing: '.14em', color: 'var(--c-ink-3)' }}>
                <div className="spinner" /> LOADING SUPERVISORS…
              </div>
            </td></tr>
          ) : error ? (
            <tr><td colSpan={9} style={{ textAlign: 'center', padding: '2rem', color: 'var(--c-danger)', fontFamily: 'var(--font-mono)', fontSize: 11 }}>{error}</td></tr>
          ) : filteredSupervisors.length === 0 ? (
            <tr><td colSpan={9} style={{ textAlign: 'center', padding: '2rem', fontFamily: 'var(--font-mono)', fontSize: 11, letterSpacing: '.14em', color: 'var(--c-ink-3)', textTransform: 'uppercase' }}>
              {searchTerm || selectedAreaFilter !== 'All' || selectedStatusFilter !== 'All'
                ? 'NO SUPERVISORS MATCH YOUR FILTERS'
                : 'NO SUPERVISORS FOUND'}
            </td></tr>
          ) : filteredSupervisors.map((supervisor) => {
            const canonicalId = supervisor.ixoraone_employee_id;
            const legacyId = supervisor.ixora_user_id;
            const isInactive = supervisor.userStatus === 'blocked' || supervisor.userStatus === 'inactive';
            return (
              <tr key={supervisor.id}>
                <td style={{ textAlign: 'center' }}>{initialsAvatar(supervisor.display_name, canonicalId || legacyId)}</td>
                <td className="num">
                  {canonicalId ? (
                    <>
                      <span style={{ color: 'var(--c-ink)' }}>{canonicalId}</span>
                      {legacyId && legacyId !== canonicalId && (
                        <span style={{ marginLeft: 6, color: 'var(--c-ink-4)', fontSize: 10 }}>({legacyId})</span>
                      )}
                    </>
                  ) : (
                    <span style={{ color: 'var(--c-ink-3)' }}>{legacyId || '—'}</span>
                  )}
                </td>
                <td>{supervisor.display_name || '—'}</td>
                <td>{supervisorZoneName(supervisor) ?? (supervisor.ixora_allotment ? getAreaName(supervisor.ixora_allotment) : '—')}</td>
                <td className="num">{supervisor.phone_number || '—'}</td>
                <td className="num">
                  {supervisor.lastChecklistDocDate
                    ? new Date(supervisor.lastChecklistDocDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
                    : '—'}
                </td>
                <td>
                  {supervisor.userRole === 'siteManager'
                    ? <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10, letterSpacing: '.10em', color: 'var(--c-cool-2)', textTransform: 'uppercase' }}>Site Mgr</span>
                    : <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10, letterSpacing: '.10em', color: 'var(--c-ink-3)', textTransform: 'uppercase' }}>Supervisor</span>}
                </td>
                <td>
                  {isInactive
                    ? <span className="status-nd">INACTIVE</span>
                    : <span className="status-done">ACTIVE</span>}
                </td>
                <td>
                  <a
                    href={ixoraOneLinkFor(supervisor)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="db-bare-link"
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
                    title="Open this employee in IxoraOne"
                  >
                    MANAGE
                    <OpenInNewIcon style={{ fontSize: 12 }} />
                  </a>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );

  const selectStyle = {
    width: 'auto', minHeight: 32, padding: '4px 10px',
    fontFamily: 'var(--font-mono)', fontSize: 11, letterSpacing: '.10em', textTransform: 'uppercase',
    border: '1px solid var(--c-line-2)', background: 'var(--c-bg)', color: 'var(--c-ink-2)', fontWeight: 600,
    cursor: 'pointer',
  };

  // -------------------------------------------------------------------------
  // Phone mode
  // -------------------------------------------------------------------------

  if (mode === 'phone') {
    return (
      <div className="db-page-phone">
        <PhoneTopStrip />

        <div className="db-ribbon-phone">
          <div className="title">
            SUPERVISORS DIRECTORY
            <div style={{ marginTop: 4, color: 'var(--c-ink-3)', letterSpacing: '.10em', fontWeight: 500 }}>
              {filteredSupervisors.length} OF {supervisors.length}
            </div>
          </div>
          <div style={{ marginTop: 8, marginBottom: 8 }}>
            <ManagedInIxoraOneBanner />
          </div>
          <div className="pills">
            <button onClick={handleExport} className="db-pill">EXPORT XLS</button>
          </div>
          <div style={{ marginTop: 10 }}>
            <div className="db-form-row">
              <label>SEARCH NAME OR ID</label>
              <div style={{ position: 'relative' }}>
                <SearchIcon style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--c-ink-3)', fontSize: 16 }} />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{ paddingLeft: 32 }}
                  placeholder="SEARCH"
                />
              </div>
            </div>
            <div className="db-form-row">
              <label>ROLE</label>
              <select value={selectedRoleFilter} onChange={(e) => setSelectedRoleFilter(e.target.value)}>
                <option value="All">ROLE · ALL</option>
                <option value="Supervisor">SUPERVISOR</option>
                <option value="Site Manager">SITE MANAGER</option>
              </select>
            </div>
            <div className="db-form-row">
              <label>AREA</label>
              <select value={selectedAreaFilter} onChange={(e) => setSelectedAreaFilter(e.target.value)}>
                {areaOptions.map(a => <option key={a} value={a}>{a === 'All' ? 'AREA · ALL' : a.toUpperCase()}</option>)}
              </select>
            </div>
            <div className="db-form-row" style={{ marginBottom: 0 }}>
              <label>STATUS</label>
              <select value={selectedStatusFilter} onChange={(e) => setSelectedStatusFilter(e.target.value)}>
                <option value="All">STATUS · ALL</option>
                <option value="Active">ACTIVE</option>
                <option value="Blocked">INACTIVE</option>
              </select>
            </div>
          </div>
        </div>

        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 10, padding: '2rem', fontFamily: 'var(--font-mono)', fontSize: 11, letterSpacing: '.14em', color: 'var(--c-ink-3)' }}>
            <div className="spinner" /> LOADING SUPERVISORS…
          </div>
        ) : error ? (
          <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--c-danger)', fontFamily: 'var(--font-mono)', fontSize: 11 }}>{error}</div>
        ) : filteredSupervisors.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '2rem', fontFamily: 'var(--font-mono)', fontSize: 11, letterSpacing: '.14em', color: 'var(--c-ink-3)', textTransform: 'uppercase' }}>
            {searchTerm || selectedAreaFilter !== 'All' || selectedStatusFilter !== 'All'
              ? 'NO SUPERVISORS MATCH YOUR FILTERS'
              : 'NO SUPERVISORS FOUND'}
          </div>
        ) : (
          <div className="db-list-phone">
            {filteredSupervisors.map((supervisor) => {
              const canonicalId = supervisor.ixoraone_employee_id;
              const legacyId = supervisor.ixora_user_id;
              const isInactive = supervisor.userStatus === 'blocked' || supervisor.userStatus === 'inactive';
              return (
                <a
                  key={supervisor.id}
                  href={ixoraOneLinkFor(supervisor)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="item"
                  style={{ width: '100%', textAlign: 'left', background: 'transparent', textDecoration: 'none', color: 'inherit', padding: '14px 16px', borderBottom: '1px solid var(--c-line-soft)', display: 'flex', flexDirection: 'column', gap: 4 }}
                >
                  <div className="row1">
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0, flex: 1 }}>
                      {initialsAvatar(supervisor.display_name, canonicalId || legacyId)}
                      <div className="nm" style={{ minWidth: 0 }}>{supervisor.display_name || '—'}</div>
                    </div>
                    {isInactive
                      ? <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10, letterSpacing: '.12em', textTransform: 'uppercase', fontWeight: 700, color: 'var(--c-danger)' }}>INACTIVE</span>
                      : <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10, letterSpacing: '.12em', textTransform: 'uppercase', fontWeight: 700, color: 'var(--c-success)' }}>ACTIVE</span>}
                  </div>
                  <div className="stats-row">
                    <div>
                      <span className="l">ID</span>
                      <span className="v">{canonicalId || legacyId || '—'}</span>
                    </div>
                    <div>
                      <span className="l">AREA</span>
                      <span className="v" style={{ fontFamily: "'Geist', sans-serif", fontSize: 12 }}>{supervisorZoneName(supervisor) ?? (supervisor.ixora_allotment ? getAreaName(supervisor.ixora_allotment) : '—')}</span>
                    </div>
                    <div>
                      <span className="l">ROLE</span>
                      <span className="v" style={{ fontFamily: "'Geist', sans-serif", fontSize: 12 }}>{supervisor.userRole === 'siteManager' ? 'Site Mgr' : 'Supervisor'}</span>
                    </div>
                  </div>
                  <div className="sub" style={{ marginTop: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, minWidth: 0 }}>
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>{supervisor.phone_number || '—'}</span>
                    <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10, letterSpacing: '.12em', textTransform: 'uppercase', fontWeight: 600, color: '#EA580C', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                      MANAGE IN IXORAONE
                      <OpenInNewIcon style={{ fontSize: 12 }} />
                    </span>
                  </div>
                </a>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  // -------------------------------------------------------------------------
  // Desk mode
  // -------------------------------------------------------------------------

  return (
    <div className="db-page">
      <DepartureTopStrip />

      <div className="db-ribbon">
        <div className="db-ribbon-title">
          SUPERVISORS DIRECTORY
          <span style={{ color: 'var(--c-ink-3)', fontWeight: 500, letterSpacing: '.10em', marginLeft: 12 }}>
            {filteredSupervisors.length} OF {supervisors.length}
          </span>
        </div>
        <div className="db-ribbon-actions">
          <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
            <SearchIcon style={{ position: 'absolute', left: 8, color: 'var(--c-ink-3)', fontSize: 16 }} />
            <input type="text" placeholder="SEARCH NAME OR ID" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
              style={{ ...selectStyle, paddingLeft: 28, minWidth: 200, fontWeight: 400 }} />
          </div>
          <select value={selectedRoleFilter} onChange={(e) => setSelectedRoleFilter(e.target.value)} style={selectStyle}>
            <option value="All">ROLE · ALL</option>
            <option value="Supervisor">SUPERVISOR</option>
            <option value="Site Manager">SITE MANAGER</option>
          </select>
          <select value={selectedAreaFilter} onChange={(e) => setSelectedAreaFilter(e.target.value)} style={selectStyle}>
            {areaOptions.map(a => <option key={a} value={a}>{a === 'All' ? 'AREA · ALL' : a.toUpperCase()}</option>)}
          </select>
          <select value={selectedStatusFilter} onChange={(e) => setSelectedStatusFilter(e.target.value)} style={selectStyle}>
            <option value="All">STATUS · ALL</option>
            <option value="Active">ACTIVE</option>
            <option value="Blocked">INACTIVE</option>
          </select>
          <button onClick={handleExport} className="db-pill">EXPORT XLS</button>
        </div>
      </div>

      <div style={{ padding: '12px var(--pad-h) 0' }}>
        <ManagedInIxoraOneBanner />
      </div>

      <section style={{ padding: 'var(--sp-6) var(--pad-h) var(--sp-12)', overflowX: 'auto' }}>
        {renderTableSection()}
      </section>
    </div>
  );
};

export default ManageSupervisors;
